import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * The card's parts, in the one order every entity's card keeps (decision 101, #401): what it is,
 * its status and acts, the facts, then the related entities as links, then its story. A finance
 * movement, a clinical visit and a role use the same parts as the invitation does.
 */

export function EntityHead({
  titleId,
  kind,
  title,
  sub,
  lead,
  status,
  actions,
}: {
  readonly titleId: string;
  /** What the entity is, in one word: «Invito», «Ruolo», «Movimento». */
  readonly kind: string;
  readonly title: string;
  readonly sub?: string | null;
  /** An avatar or an icon before the title. */
  readonly lead?: ReactNode;
  /** The status badge and, after it, its one line of context. */
  readonly status?: ReactNode;
  /** Only the acts this caller may use on it. */
  readonly actions?: ReactNode;
}) {
  return (
    <div className="entity-head">
      <span className="entity-kind">{kind}</span>
      <div className="flex items-center gap-3">
        {lead}
        <span className="flex min-w-0 flex-col">
          <h2 id={titleId} className="entity-title">
            {title}
          </h2>
          {sub ? <span className="entity-sub">{sub}</span> : null}
        </span>
      </div>
      {status ? <div className="flex flex-wrap items-center gap-2">{status}</div> : null}
      {actions ? <div className="flex flex-wrap gap-2 pt-1.5">{actions}</div> : null}
    </div>
  );
}

export type EntityFact = { readonly label: string; readonly value: ReactNode };

export function EntityFacts({ facts }: { readonly facts: readonly EntityFact[] }) {
  return (
    <dl className="entity-facts">
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export type RelatedRow = {
  readonly key: string;
  readonly to: string;
  readonly label: string;
  readonly sub?: string | null;
  readonly lead?: ReactNode;
};

/** Related entities: a count, link rows, and «Vedi tutti» to their list filtered on this one. */
export function RelatedSection({
  id,
  title,
  count,
  rows,
  more,
}: {
  readonly id: string;
  readonly title: string;
  readonly count: number;
  readonly rows: readonly RelatedRow[];
  readonly more?: { readonly to: string; readonly label: string };
}) {
  return (
    <section aria-labelledby={id} className="entity-section">
      <div className="flex items-center justify-between gap-3">
        <h3 id={id} className="entity-section-title">
          {title} <span className="entity-section-count">· {count}</span>
        </h3>
        {more ? (
          <Link to={more.to} className="entity-more">
            {more.label}
            <ChevronRight aria-hidden="true" className="size-3.5" />
          </Link>
        ) : null}
      </div>
      <ul className="entity-related">
        {rows.map((row) => (
          <li key={row.key}>
            <Link to={row.to} className="entity-related-row">
              {row.lead}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="entity-related-label">{row.label}</span>
                {row.sub ? <span className="entity-related-sub">{row.sub}</span> : null}
              </span>
              <ChevronRight aria-hidden="true" className="entity-related-arrow size-3.5" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export type TimelineEvent = {
  readonly key: string;
  readonly text: string;
  readonly when: string;
  readonly tone?: 'brand' | 'success' | 'muted' | 'warning';
};

export function EntityTimeline({
  id,
  title,
  events,
}: {
  readonly id: string;
  readonly title: string;
  readonly events: readonly TimelineEvent[];
}) {
  return (
    <section aria-labelledby={id} className="entity-section">
      <h3 id={id} className="entity-section-title">
        {title}
      </h3>
      <ol className="entity-timeline">
        {events.map((event) => (
          <li key={event.key} data-tone={event.tone ?? 'brand'}>
            <span className="entity-timeline-text">{event.text}</span>
            <span className="entity-timeline-when">{event.when}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
